import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  TextField,
  Grid
} from '@mui/material';
import { Settings as SettingsIcon, Crop as CropIcon, Public as PublicIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { BACKEND_URL } from '../config';

const AdminSettings = () => {
  const theme = useTheme();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/admin-settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }

      const data = await response.json();
      const settingsMap = {};
      data.forEach(setting => {
        settingsMap[setting.setting_key] = {
          value: setting.setting_value,
          type: setting.setting_type
        };
      });
      
      setSettings(settingsMap);
    } catch (err) {
      setError('Failed to load admin settings: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (settingKey, value, settingType = null) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`/admin-settings/${settingKey}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          setting_value: value,
          setting_type: settingType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update setting');
      }

      setSettings(prev => ({
        ...prev,
        [settingKey]: {
          value: value,
          type: settingType || prev[settingKey]?.type || 'boolean'
        }
      }));
      
      setSuccess(`${settingKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} updated successfully`);
    } catch (err) {
      setError('Failed to update setting: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (settingKey) => (event) => {
    const newValue = event.target.checked;
    updateSetting(settingKey, newValue, 'boolean');
  };

  const handleNumberChange = (settingKey) => (event) => {
    const newValue = parseInt(event.target.value) || 0;
    updateSetting(settingKey, newValue, 'integer');
  };

  const handleTextChange = (settingKey) => (event) => {
    const newValue = event.target.value;
    updateSetting(settingKey, newValue, 'string');
  };

  const getSettingValue = (settingKey, defaultValue = false) => {
    return settings[settingKey]?.value ?? defaultValue;
  };

  const getSettingType = (settingKey) => {
    return settings[settingKey]?.type ?? 'boolean';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: theme.palette.text.primary }}>
        Admin Settings
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3, color: theme.palette.text.secondary }}>
        Manage system-wide settings for the UFO Planet application.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Planet Generation Safe Mode */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon />
              Planet Generation Safe Mode
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={getSettingValue('planet_generation_safe_mode', false)}
                  onChange={handleToggle('planet_generation_safe_mode')}
                  disabled={saving}
                />
              }
              label="Enable Safe Mode"
            />
            
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
              When enabled, uses pre-generated images instead of calling the AI API for planet generation
            </Typography>
          </Paper>
        </Grid>

        {/* Attachments Generation Safe Mode */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon />
              Attachments Generation Safe Mode
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <FormControlLabel
              control={
                <Switch
                  checked={getSettingValue('attachments_generation_safe_mode', false)}
                  onChange={handleToggle('attachments_generation_safe_mode')}
                  disabled={saving}
                />
              }
              label="Enable Safe Mode"
            />
            
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
              When enabled, uses pre-generated images instead of calling the AI API for attachment generation
            </Typography>
          </Paper>
        </Grid>

        {/* Canvas Width */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CropIcon />
              Canvas Width
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <TextField
              label="Width"
              type="number"
              value={getSettingValue('canvas_width', 2160)}
              onChange={handleNumberChange('canvas_width')}
              disabled={saving}
              fullWidth
              InputProps={{
                endAdornment: <Typography variant="caption">px</Typography>
              }}
            />
            
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
              Width of the main game canvas where attachments are placed
            </Typography>
          </Paper>
        </Grid>

        {/* Canvas Height */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CropIcon />
              Canvas Height
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <TextField
              label="Height"
              type="number"
              value={getSettingValue('canvas_height', 2160)}
              onChange={handleNumberChange('canvas_height')}
              disabled={saving}
              fullWidth
              InputProps={{
                endAdornment: <Typography variant="caption">px</Typography>
              }}
            />
            
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
              Height of the main game canvas where attachments are placed
            </Typography>
          </Paper>
        </Grid>

        {/* Earth Width */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PublicIcon />
              Earth Width
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <TextField
              label="Width"
              type="number"
              value={getSettingValue('earth_width', 1006)}
              onChange={handleNumberChange('earth_width')}
              disabled={saving}
              fullWidth
              InputProps={{
                endAdornment: <Typography variant="caption">px</Typography>
              }}
            />
            
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
              Width of the earth/planet background image
            </Typography>
          </Paper>
        </Grid>

        {/* Earth Height */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PublicIcon />
              Earth Height
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <TextField
              label="Height"
              type="number"
              value={getSettingValue('earth_height', 1006)}
              onChange={handleNumberChange('earth_height')}
              disabled={saving}
              fullWidth
              InputProps={{
                endAdornment: <Typography variant="caption">px</Typography>
              }}
            />
            
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
              Height of the earth/planet background image
            </Typography>
          </Paper>
        </Grid>

        {/* OpenAI API Key */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon />
              OpenAI API Key
            </Typography>
            
            <Divider sx={{ mb: 2 }} />
            
            <TextField
              label="API Key"
              type="password"
              value={getSettingValue('openai_api_key', '')}
              onChange={handleTextChange('openai_api_key')}
              disabled={saving}
              fullWidth
              multiline
              rows={2}
              placeholder="sk-..."
              helperText="Enter your OpenAI API key. This is required for AI image generation."
            />
            
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 2 }}>
              This API key is used for generating planets and attachments using OpenAI's DALL-E model. 
              Keep this key secure and never share it publicly.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminSettings; 